import { InitialSearchState } from '@constants/jobInitialSearch';
import * as api from '@company/services/job/api';
import { ApplicantsType } from '@constants/applicantsType';
import IJobDetail from '@th-types/job.detail.type';
import DATE_FORMATS from '@constants/dateFormat';
import { formatLocalDate, UTC_TIMEZONE } from '@utils/DateUtils';
import QueryKeys from '@constants/queryKeys';
import { AxiosError } from 'axios';
import { debounce } from 'lodash';
import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { fetchApplicationCounters } from '@company/services/application/api';
import ApplicantFilter from '@company/types/applicant-filter.type';

interface IJobContext {
  jobTimezone: string;
  newHired: boolean;
  hiredCount: number;
  isLoading?: boolean;
  key: ApplicantsType;
  hiddenCount: number;
  newOffered: boolean;
  activeJobId: number;
  offeredCount: number;
  appliedCount: number;
  openPositions: number;
  job: IJobDetail | undefined;
  initialSearch: InitialSearchState;
  applicantFilters: ApplicantFilter;
  error?: AxiosError<unknown, any> | null;
  refetch: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<IJobDetail, AxiosError<unknown, any>>>;
  getJobYear: () => string;
  setJobParamId(id: string): void;
  setActiveJobId(id: number): void;
  fetchApplicantCounters: () => void;
  setNewHired: (value: boolean) => void;
  setHiredCount: (value: number) => void;
  setHiddenCount: (value: number) => void;
  setNewOffered: (value: boolean) => void;
  setKey: (newKey: ApplicantsType) => void;
  setOfferedCount: (value: number) => void;
  setAppliedCount: (value: number) => void;
  getLocalEndDate: (format?: string) => string;
  getLocalStartDate: (format?: string) => string;
  isInternalMember: (memberId: number) => boolean;
  setApplicantFilters: (filter: ApplicantFilter) => void;
  setInitialSearch: (initialSearchState: InitialSearchState) => void;
}

export const initialApplicantFilters = {
  order: 'desc',
  sortBy: 'dateApplied',
  gender: [],
  country: [],
  database: [],
  nameOrEmail: '',
  willingToTravel: undefined,
  travelLongerPeriod: undefined,
};

const JobContext: IJobContext | any = createContext({});

interface ProviderProps {
  children: React.ReactNode;
}

export function JobProvider({ children }: ProviderProps) {
  const [activeJobId, setActiveJobId] = useState<number>(0);
  const [debouncedJobId, setDebouncedJobId] = useState<number>(0);

  const setJobParamId = useCallback((rawParamId: string) => {
    const sanitizedId = rawParamId ? rawParamId.split('--').pop() : '';
    const parsedId = sanitizedId ? parseInt(sanitizedId, 10) : 0;
    setActiveJobId(parsedId);
  }, []);

  // Create a debounced function to update the debounced job ID
  const debouncedSetJobId = useMemo(
    () =>
      debounce((jobId: number) => {
        setDebouncedJobId(jobId);
      }, 150),
    []
  );

  // Debounce the job ID changes to prevent rapid API calls
  useEffect(() => {
    if (activeJobId > 0) {
      debouncedSetJobId(activeJobId);
    }

    // Cleanup function to cancel pending debounced calls
    return () => {
      debouncedSetJobId.cancel();
    };
  }, [activeJobId, debouncedSetJobId]);

  const {
    isLoading,
    error,
    data: job,
    refetch,
  } = useQuery<IJobDetail, AxiosError, IJobDetail>({
    queryKey: [`${QueryKeys.COMPANY_JOB_DETAIL}_${activeJobId}`, activeJobId], // add activeJobId as a dependency
    queryFn: () => api.fetchJobDetail(activeJobId),
    enabled: activeJobId > 0,
  });

  const [key, setKey] = useState<ApplicantsType>(ApplicantsType.APPLIED);
  const [hiredCount, setHiredCount] = useState(0);
  const [offeredCount, setOfferedCount] = useState(0);
  const [appliedCount, setAppliedCount] = useState(0);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [openPositions, setOpenPositions] = useState(0);
  const [newHired, setNewHired] = useState(false);
  const [newOffered, setNewOffered] = useState(false);
  const [initialSearch, setInitialSearch] = useState<InitialSearchState>(
    InitialSearchState.NOT_STARTED
  );
  const [applicantFilters, setApplicantFilters] = useState<ApplicantFilter>(
    initialApplicantFilters
  );

  // Add React Query for counters to provide better caching
  const { data: countersData, refetch: refetchCounters } = useQuery({
    queryKey: [QueryKeys.COMPANY_JOB_COUNTERS, debouncedJobId],
    queryFn: async ({ queryKey }) => {
      const [, jobId] = queryKey;
      return fetchApplicationCounters(jobId as number);
    },
    enabled: debouncedJobId > 0,
  });

  // Update counters when the query data changes
  useEffect(() => {
    if (countersData) {
      setHiredCount(countersData.hiredAssignments);
      setOfferedCount(countersData.jobOffers);
      setHiddenCount(countersData.declinedAssignments);
      setAppliedCount(countersData.jobApplicants);
    }
  }, [countersData]);

  useEffect(() => {
    if (job) {
      setOpenPositions(job.openPositions - hiredCount);
    }
  }, [hiredCount, job]);

  const jobTimezone = useMemo(() => {
    return job?.address?.timezoneId ?? UTC_TIMEZONE;
  }, [job?.address?.timezoneId]);

  // Keep the old fetchApplicantCounters for backward compatibility, but now it just triggers a refetch
  const fetchApplicantCounters = useCallback(async () => {
    return refetchCounters();
  }, [refetchCounters]);

  const isInternalMember = useCallback(
    (memberId: number): boolean => {
      return (
        job?.members?.some(
          (member) => member.id === memberId && member.isInternal
        ) || false
      );
    },
    [job]
  );

  const getLocalStartDate = useCallback(
    (dateFormat: string = DATE_FORMATS.SHORT_DATE_FORMAT): string => {
      return formatLocalDate(job!.localizedStart, dateFormat, UTC_TIMEZONE);
    },
    [job]
  );

  // Does not handle ongoing job logic here. Do that in the caller of this function
  const getLocalEndDate = useCallback(
    (dateFormat: string = DATE_FORMATS.SHORT_DATE_FORMAT): string => {
      return formatLocalDate(job!.localizedEnd, dateFormat, UTC_TIMEZONE);
    },
    [job]
  );

  const getJobYear = useCallback(() => {
    const currentYear = new Date().getFullYear();
    const jobYear = job?.start ? new Date(job.start).getFullYear() : undefined;
    return jobYear !== undefined && currentYear !== jobYear
      ? `${jobYear.toString().slice(-2)}' `
      : '';
  }, [job]);

  const contextValue: IJobContext = useMemo(() => {
    return {
      job,
      key,
      error,
      newHired,
      isLoading,
      hiredCount,
      newOffered,
      jobTimezone,
      activeJobId,
      hiddenCount,
      offeredCount,
      appliedCount,
      openPositions,
      initialSearch,
      applicantFilters,
      setKey,
      refetch,
      getJobYear,
      setNewHired,
      setJobParamId,
      setNewOffered,
      setHiredCount,
      setHiddenCount,
      setActiveJobId,
      getLocalEndDate,
      setOfferedCount,
      setAppliedCount,
      setInitialSearch,
      isInternalMember,
      getLocalStartDate,
      setApplicantFilters,
      fetchApplicantCounters,
    };
  }, [
    job,
    key,
    error,
    newHired,
    isLoading,
    hiredCount,
    newOffered,
    jobTimezone,
    activeJobId,
    hiddenCount,
    offeredCount,
    appliedCount,
    openPositions,
    initialSearch,
    applicantFilters,
    setKey,
    refetch,
    getJobYear,
    setNewHired,
    setJobParamId,
    setNewOffered,
    setHiredCount,
    setHiddenCount,
    setActiveJobId,
    getLocalEndDate,
    setOfferedCount,
    setAppliedCount,
    setInitialSearch,
    isInternalMember,
    getLocalStartDate,
    setApplicantFilters,
    fetchApplicantCounters,
  ]);
  return (
    <JobContext.Provider value={contextValue}>{children}</JobContext.Provider>
  );
}

export function useJobContext() {
  const context: IJobContext = useContext(JobContext);
  if (context === undefined) {
    throw new Error(
      'useJobContext must be used within a JobProvider. Wrap a parent component in <JobProvider> to fix this error.'
    );
  }
  return context;
}
